import * as THREE from "three";
import PacedDetailModulesService from "../../../../API/organization/PacedDetailModulesService";
import {calcValueOfStringWithVar} from "../../../customer/processing/DetailProcessing/Canvas/CanvasCalcPositions/calcValueOfStringWithVar";
import {
    axesForPersist,
    cloneAxes,
    IDENTITY_AXES,
    isCardinalAxes,
    normalizeAxesWithFaceFlip,
} from "./orientationAxes";
import {MM_TO_M} from "./panelMeshTransform";
import {compositionMaterialType} from "../../../../data/CompositionMaterialType";
import {
    normalizeLocalRotationAngles,
    panelHasObliqueTilt,
} from "./localRotation";
import {sortDetailsBySequenceNumber} from "../../../../utils/processing/orderContourPoints";

/** JSON в variables[] paced-detail-modules: reference + угол одноосевого поворота. */
export const MODULE3D_PANEL_ROTATION_VAR = "module3d_panel_rotation";

const snapComponent = (value) => {
    if (Math.abs(value) < 1e-8) return 0;
    if (Math.abs(Math.abs(value) - 1) < 1e-8) return Math.sign(value);
    return Number(value.toFixed(6));
};

const vectorToTuple = (vector) => [
    snapComponent(vector.x),
    snapComponent(vector.y),
    snapComponent(vector.z),
];

const CARDINAL_AXES = [
    new THREE.Vector3(1, 0, 0),
    new THREE.Vector3(-1, 0, 0),
    new THREE.Vector3(0, 1, 0),
    new THREE.Vector3(0, -1, 0),
    new THREE.Vector3(0, 0, 1),
    new THREE.Vector3(0, 0, -1),
];

/** Серверные оси приводим к ближайшему базису с поворотами по 90°. */
const snapAxesToCardinalBasis = (axes) => {
    const rawX = new THREE.Vector3(...axes.x).normalize();
    const rawY = new THREE.Vector3(...axes.y).normalize();
    const rawZ = new THREE.Vector3(...axes.z).normalize();
    let best = null;

    for (const x of CARDINAL_AXES) {
        for (const y of CARDINAL_AXES) {
            if (Math.abs(x.dot(y)) > 1e-8) continue;
            const rightZ = new THREE.Vector3().crossVectors(x, y);
            for (const z of [rightZ, rightZ.clone().negate()]) {
                const score = x.dot(rawX) + y.dot(rawY) + z.dot(rawZ);
                if (!best || score > best.score) {
                    best = {score, x, y, z};
                }
            }
        }
    }

    return {
        x: vectorToTuple(best.x),
        y: vectorToTuple(best.y),
        z: vectorToTuple(best.z),
    };
};

const readPanelRotationFromVariables = (variables) => {
    const row = asVariablesList(variables).find(
        (v) => v?.name === MODULE3D_PANEL_ROTATION_VAR,
    );
    if (!row) return null;
    const raw = row.pattern ?? row.value;
    if (raw == null || String(raw).trim() === "") return null;
    try {
        const data = JSON.parse(String(raw));
        if (!data || typeof data !== "object") return null;
        if (!data.referenceAxes || !data.localRotationAngles) return null;
        return {
            referenceAxes: cloneAxes(data.referenceAxes),
            localRotationAngles: normalizeLocalRotationAngles(data.localRotationAngles),
        };
    } catch {
        return null;
    }
};

const shouldPersistPanelRotation = (referenceAxes, localRotationAngles, persistAxes) => {
    if (!referenceAxes || !localRotationAngles) return false;
    const angles = normalizeLocalRotationAngles(localRotationAngles);
    return panelHasObliqueTilt(referenceAxes, angles)
        || !isCardinalAxes(persistAxes || IDENTITY_AXES);
};

const mergePanelRotationVariables = (
    prevVariables,
    referenceAxes,
    localRotationAngles,
    persistAxes,
) => {
    const prev = asVariablesList(prevVariables);
    const prevRow = prev.find((v) => v?.name === MODULE3D_PANEL_ROTATION_VAR);
    const without = prev.filter((v) => v?.name !== MODULE3D_PANEL_ROTATION_VAR);
    if (!shouldPersistPanelRotation(referenceAxes, localRotationAngles, persistAxes)) {
        return without;
    }
    const payload = JSON.stringify({
        referenceAxes: cloneAxes(referenceAxes),
        localRotationAngles: normalizeLocalRotationAngles(localRotationAngles),
    });
    return [
        ...without,
        {
            ...(prevRow?.id != null ? {id: prevRow.id} : {}),
            name: MODULE3D_PANEL_ROTATION_VAR,
            pattern: payload,
        },
    ];
};

export const asVariablesList = (mapOrList) => {
    if (!mapOrList) return [];
    if (Array.isArray(mapOrList)) return mapOrList.filter(Boolean);
    if (typeof mapOrList === "object") return Object.values(mapOrList).filter(Boolean);
    return [];
};

const isPlainNumberString = (str) => /^-?\d+(\.\d+)?([eE][+-]?\d+)?$/.test(String(str).trim());

/**
 * Координата точки: число, pattern на самой точке или выражение через variables.
 */
const resolveScalar = (raw, coordName, pointVariables, variablesList) => {
    const pointVars = asVariablesList(pointVariables);
    const named = pointVars.find((v) => v?.name === coordName);
    const candidates = [
        named?.pattern,
        named?.value,
        raw,
    ];

    for (const candidate of candidates) {
        if (candidate == null || candidate === "") continue;

        if (typeof candidate === "number" && Number.isFinite(candidate)) {
            return candidate;
        }

        const str = String(candidate).trim();
        if (!str) continue;

        if (isPlainNumberString(str)) {
            const n = Number(str);
            if (Number.isFinite(n)) return n;
            continue;
        }

        try {
            const n = calcValueOfStringWithVar(variablesList, str);
            if (Number.isFinite(n)) return n;
        } catch {
            // следующий кандидат
        }
    }

    const fallback = Number(raw);
    return Number.isFinite(fallback) ? fallback : 0;
};

const resolvePointMm = (point, variablesList) => {
    if (!point || typeof point !== "object") return null;
    const mergedVars = [
        ...variablesList,
        ...asVariablesList(point.variables),
    ];
    return {
        x: resolveScalar(point.x, "x", point.variables, mergedVars),
        y: resolveScalar(point.y, "y", point.variables, mergedVars),
        z: resolveScalar(point.z, "z", point.variables, mergedVars),
    };
};

const resolveAxisTuple = (point, variablesList) => {
    const mm = resolvePointMm(point, variablesList);
    if (!mm) return null;
    const vector = new THREE.Vector3(mm.x, mm.y, mm.z);
    if (vector.lengthSq() < 1e-12) return null;
    return vectorToTuple(vector.normalize());
};

/**
 * DTO с бэка → поза для сцены (cube-local, м + базис + faceFlip).
 * faceFlip=false: локальный +Y панели = нормаль TOP (как в 2D).
 * @returns {{localPosition, axes, faceFlip, id, raw}|null}
 */
export const getPacedDtoHide = (dto) => Boolean(dto?.hide ?? dto?.raw?.hide);

/** Если в DTO нет поля hide — оставляем текущее состояние на сцене (GET by-module-id часто без hide). */
export const resolvePacedDtoHidden = (dto, fallbackHidden = false) => {
    if (dto == null || typeof dto !== "object") {
        return Boolean(fallbackHidden);
    }
    if (Object.prototype.hasOwnProperty.call(dto, "hide")) {
        return Boolean(dto.hide);
    }
    if (dto.raw && typeof dto.raw === "object" && Object.prototype.hasOwnProperty.call(dto.raw, "hide")) {
        return Boolean(dto.raw.hide);
    }
    return Boolean(fallbackHidden);
};

export const parsePacedDetailModuleDto = (dto) => {
    if (!dto || typeof dto !== "object") return null;

    const variablesList = asVariablesList(dto.variables);
    const anchorMm = resolvePointMm(dto.anchorPoint, variablesList);
    const axisX = resolveAxisTuple(dto.axesX, variablesList);
    const axisY = resolveAxisTuple(dto.axesY, variablesList);
    const axisZ = resolveAxisTuple(dto.axesZ, variablesList);

    const hasAxes = Boolean(axisX || axisY || axisZ);
    const hasAnchor = Boolean(anchorMm);

    if (!hasAxes && !hasAnchor) return null;

    let axes = null;
    let faceFlip = false;
    if (hasAxes) {
        const rawAxes = cloneAxes(IDENTITY_AXES);
        if (axisX) rawAxes.x = axisX;
        if (axisY) rawAxes.y = axisY;
        if (axisZ) rawAxes.z = axisZ;
        const axesForNormalize = isCardinalAxes(rawAxes)
            ? snapAxesToCardinalBasis(rawAxes)
            : rawAxes;
        const normalized = normalizeAxesWithFaceFlip(
            axesForNormalize,
            false,
        );
        axes = normalized.axes;
        faceFlip = normalized.faceFlip;
    }

    const localPosition = hasAnchor
        ? {
            x: anchorMm.x * MM_TO_M,
            y: anchorMm.y * MM_TO_M,
            z: anchorMm.z * MM_TO_M,
        }
        : null;

    const rotationMeta = readPanelRotationFromVariables(dto.variables);

    return {
        id: dto.id ?? null,
        localPosition,
        axes,
        faceFlip,
        referenceAxes: rotationMeta?.referenceAxes ?? null,
        localRotationAngles: rotationMeta?.localRotationAngles ?? null,
        hide: Boolean(dto.hide),
        raw: dto,
    };
};

const metersToMm = (meters) => {
    const n = Number(meters);
    if (!Number.isFinite(n)) return 0;
    return Math.round((n / MM_TO_M) * 1000) / 1000;
};

const toPointDto = (prevPoint, x, y, z) => {
    const point = {
        x: String(Number.isFinite(x) ? x : 0),
        y: String(Number.isFinite(y) ? y : 0),
        z: String(Number.isFinite(z) ? z : 0),
        variables: asVariablesList(prevPoint?.variables),
    };
    if (prevPoint?.id != null) {
        point.id = prevPoint.id;
    }
    return point;
};

const toCreatePointDto = (x, y, z) => ({
    x: String(Number.isFinite(x) ? x : 0),
    y: String(Number.isFinite(y) ? y : 0),
    z: String(Number.isFinite(z) ? z : 0),
    variables: [],
});

const axisTuple = (axes, key) => {
    const t = axes?.[key] || IDENTITY_AXES[key];
    return [
        Number(t?.[0]) || 0,
        Number(t?.[1]) || 0,
        Number(t?.[2]) || 0,
    ];
};

const hasRealId = (id) => id != null && id !== "" && Number(id) !== 0;

const pacedDetailModuleByIdCache = new Map();

export const invalidatePacedDetailModuleCache = (pacedDetailModuleId) => {
    if (pacedDetailModuleId == null || pacedDetailModuleId === "") return;
    pacedDetailModuleByIdCache.delete(String(pacedDetailModuleId));
};

export const invalidateAllPacedDetailModuleCache = () => {
    pacedDetailModuleByIdCache.clear();
};

const cachePacedDetailModuleDto = (pacedDetailModuleId, data) => {
    if (!hasRealId(pacedDetailModuleId) || data == null || typeof data !== "object") return;
    pacedDetailModuleByIdCache.set(String(pacedDetailModuleId), data);
};

const isUsableId = (id) => id != null && id !== "";

const DETAIL_TABLE_FIELDS = [
    "position", "name", "length", "width", "height",
    "l1", "l2", "w1", "w2", "l1Name", "l2Name", "w1Name", "w2Name",
    "groove", "nameMaterial", "materialName", "modelSeries", "groupModel",
    "compositionMaterialType", "detailContour", "holes", "edges", "roundNotches",
    // Порядок отрисовки на 3D (sN) — не терять при flatten nested detail.
    "sequenceNumber", "sN", "sn", "priority",
];

const toDetailRows = (list) => {
    if (Array.isArray(list)) {
        return list;
    }
    if (list && typeof list === "object") {
        if (Array.isArray(list.content)) {
            return list.content;
        }
        if (Array.isArray(list.details)) {
            return list.details;
        }
        return Object.values(list).filter((item) => item && typeof item === "object" && !Array.isArray(item));
    }
    return [];
};

const flattenCompositionDetailRow = (row) => {
    if (!row || typeof row !== "object") {
        return row;
    }
    const nested = row.detail && typeof row.detail === "object" && !Array.isArray(row.detail)
        ? row.detail
        : null;
    if (!nested) {
        return row;
    }
    // nested first, then row — но null/"" с row не затирают sequenceNumber с nested.
    const flat = {...nested, ...row};
    DETAIL_TABLE_FIELDS.forEach((key) => {
        if ((flat[key] == null || flat[key] === "") && nested[key] != null && nested[key] !== "") {
            flat[key] = nested[key];
        }
    });
    if (!isUsableId(flat.id) && isUsableId(nested.id)) {
        flat.id = nested.id;
    }
    if (flat.nameMaterial == null && flat.materialName != null) {
        flat.nameMaterial = flat.materialName;
    }
    return flat;
};

const near = (a, b, eps = 1) => Math.abs(Number(a) - Number(b)) <= eps;

const edgeThickness = (edge) => {
    const name = edge?.orderEdge?.materialName || edge?.orderEdge?.shortName || "";
    const match = String(name).match(/(\d+[,.]\d+|\d+)\s*\//);
    return match ? match[1] : null;
};

const edgeLabel = (edge) => edge?.orderEdge?.shortName || edge?.orderEdge?.materialName || null;

const setSideFromEdge = (out, key, edge) => {
    if (out[key] != null && out[key] !== "") {
        return;
    }
    const thickness = edgeThickness(edge);
    const name = edgeLabel(edge);
    if (thickness == null && name == null) {
        return;
    }
    out[key] = thickness ?? name;
    out[`${key}Name`] = name;
};

const fillSidesFromEdges = (row) => {
    const edges = Array.isArray(row.edges) ? row.edges : [];
    if (!edges.length) {
        return row;
    }
    const length = Number(row.length) || 0;
    const width = Number(row.width) || 0;
    const out = {...row};
    edges.forEach((edge) => {
        const x = Number(edge.startPoint?.x) || 0;
        const y = Number(edge.startPoint?.y) || 0;
        const edgeLength = Number(edge.length) || 0;
        if (near(edgeLength, length) && near(y, 0)) {
            setSideFromEdge(out, "l1", edge);
        } else if (near(edgeLength, length) && near(y, width)) {
            setSideFromEdge(out, "l2", edge);
        } else if (near(edgeLength, width) && near(x, 0)) {
            setSideFromEdge(out, "w1", edge);
        } else if (near(edgeLength, width) && near(x, length)) {
            setSideFromEdge(out, "w2", edge);
        }
    });
    return out;
};

const normalizeCompositionDetailRow = (row) => {
    let flat = flattenCompositionDetailRow(row);
    if (!flat || typeof flat !== "object") {
        return flat;
    }
    if (flat.nameMaterial == null) {
        flat = {
            ...flat,
            nameMaterial: flat.materialName
                ?? compositionMaterialType[flat.compositionMaterialType]
                ?? flat.compositionMaterialType
                ?? null,
        };
    }
    return fillSidesFromEdges(flat);
};

/**
 * Id строки из GET by-module-id. Копируем один раз, `.id` не трогаем.
 */
export const stampCompositionDetailId = (row) => {
    if (!row || typeof row !== "object") return row;
    if (isUsableId(row.compositionDetailId)) return row;
    if (!isUsableId(row.id)) return row;
    return {...row, compositionDetailId: row.id};
};

export const stampCompositionDetailList = (list, prevList = []) => {
    const prevByPosition = new Map();
    toDetailRows(prevList).forEach((d) => {
        if (d?.position == null) return;
        if (isUsableId(d.compositionDetailId)) {
            prevByPosition.set(String(d.position), d.compositionDetailId);
        }
    });
    const stamped = toDetailRows(list).map((row) => {
        const next = normalizeCompositionDetailRow(row);
        if (!next || typeof next !== "object") return next;
        if (isUsableId(next.compositionDetailId)) return next;
        const fromPrev = next.position != null ? prevByPosition.get(String(next.position)) : null;
        const compositionDetailId = fromPrev ?? next.id;
        if (!isUsableId(compositionDetailId)) return next;
        return {...next, compositionDetailId};
    });
    // 3D и таблица: порядок по sequenceNumber (sN), а не «как пришло» с API.
    return sortDetailsBySequenceNumber(stamped);
};

/** POST paced-detail-modules?detailId= — id из GET by-module-id. */
export const getPostDetailId = (row) => {
    if (!row || typeof row !== "object") return null;
    if (isUsableId(row.compositionDetailId)) return row.compositionDetailId;
    return isUsableId(row.id) ? row.id : null;
};

/**
 * Id для GET/PUT/DELETE в path — id детали из by-module-id.
 * Если на экземпляре есть отдельный paced id, используем его как запасной вариант.
 */
export const resolvePlacementPathId = ({
    detail = null,
    inst = null,
    detailKey = null,
    pacedIdByKey = {},
    pacedModuleIdByKey = {},
    /** Для DELETE/PUT важно брать id размещения, а не id 2D детали. */
    allowDetailIdFallback = true,
}) => {
    if (detailKey != null) {
        const fromKey = pacedIdByKey[detailKey] ?? pacedModuleIdByKey[detailKey];
        if (hasRealId(fromKey)) return fromKey;
    }

    const fromInst = getPacedDetailModuleId(inst)
        ?? getPacedDetailModuleId(inst?.detail)
        ?? getPacedDetailModuleId(inst?.detailData)
        ?? null;

    if (fromInst != null) return fromInst;

    if (allowDetailIdFallback) {
        // Запасной вариант: id детали из by-module-id (если paced-id в стейте не найден).
        const fromRow = getPostDetailId(detail) ?? getPostDetailId(inst?.detail);
        if (fromRow != null) return fromRow;
    }
    return null;
};

/** Id из ответа POST (может совпадать с detailId — это нормально для path API). */
export const extractPacedRecordId = (data, response, detailId = null) => {
    const pick = (id) => (hasRealId(id) ? id : null);

    if (typeof data === "number" || typeof data === "string") {
        return pick(data);
    }

    const fromLocation = () => {
        const location = response?.headers?.location || response?.headers?.Location;
        if (typeof location !== "string") return null;
        const match = location.match(/(\d+)\s*$/);
        return match ? pick(match[1]) : null;
    };

    if (data && typeof data === "object") {
        const preferred = pick(data.pacedModuleId)
            ?? pick(data.pacedDetailModuleId)
            ?? pick(data.data?.pacedModuleId)
            ?? pick(data.data?.pacedDetailModuleId)
            ?? fromLocation();
        if (preferred != null) return preferred;
        return pick(data.id) ?? pick(data.data?.id);
    }

    return fromLocation();
};

/**
 * Поза экземпляра на сцене → тело PacedDetailModuleDto.
 * Якорь в мм, оси — направляющие.
 * @param {boolean} forCreate — POST: строковые координаты и variables без id.
 */
export const buildPacedDetailModulePayload = ({
    localPosition,
    axes,
    faceFlip = false,
    referenceAxes = null,
    localRotationAngles = null,
    existingDto = null,
    forCreate = false,
}) => {
    const prev = !forCreate && existingDto && typeof existingDto === "object"
        ? existingDto
        : null;
    const persistAxes = axesForPersist(axes, faceFlip);
    const [ax, ay, az] = [
        axisTuple(persistAxes, "x"),
        axisTuple(persistAxes, "y"),
        axisTuple(persistAxes, "z"),
    ];
    const rotationVariables = mergePanelRotationVariables(
        forCreate ? [] : prev?.variables,
        referenceAxes,
        localRotationAngles,
        persistAxes,
    );
    if (forCreate) {
        return {
            anchorPoint: toCreatePointDto(
                metersToMm(localPosition?.x),
                metersToMm(localPosition?.y),
                metersToMm(localPosition?.z),
            ),
            axesX: toCreatePointDto(ax[0], ax[1], ax[2]),
            axesY: toCreatePointDto(ay[0], ay[1], ay[2]),
            axesZ: toCreatePointDto(az[0], az[1], az[2]),
            variables: rotationVariables,
            hide: false,
        };
    }

    const payload = {
        anchorPoint: toPointDto(
            prev?.anchorPoint,
            metersToMm(localPosition?.x),
            metersToMm(localPosition?.y),
            metersToMm(localPosition?.z),
        ),
        axesX: toPointDto(prev?.axesX, ax[0], ax[1], ax[2]),
        axesY: toPointDto(prev?.axesY, ay[0], ay[1], ay[2]),
        axesZ: toPointDto(prev?.axesZ, az[0], az[1], az[2]),
        variables: rotationVariables,
        hide: Boolean(prev?.hide),
    };
    return payload;
};

/**
 * Id карточки размещения: pacedModuleId / pacedDetailModule.id.
 */
export const getPacedDetailModuleId = (detail) => {
    if (!detail || typeof detail !== "object") return null;
    if (hasRealId(detail.pacedModuleId)) return detail.pacedModuleId;
    if (hasRealId(detail.pacedDetailModuleId)) return detail.pacedDetailModuleId;
    if (hasRealId(detail.pacedUrlId)) return detail.pacedUrlId;
    const paced = detail.pacedDetailModule;
    if (paced == null) return null;
    if (typeof paced === "number" || typeof paced === "string") {
        return hasRealId(paced) ? paced : null;
    }
    if (typeof paced === "object") {
        return hasRealId(paced.id) ? paced.id : null;
    }
    return null;
};

/**
 * DELETE paced-detail-modules/{id} — id детали (как GET), либо id записи размещения.
 */
export const deletePacedDetailPlacement = async (pathId) => {
    if (!hasRealId(pathId)) return false;
    try {
        await PacedDetailModulesService.delete(pathId);
        return true;
    } catch (err) {
        const status = err?.response?.status;
        if (status === 204) return true;
        console.warn("module-3d: paced-detail-modules delete failed", err);
        return false;
    }
};

/**
 * GET paced-detail-modules/{id} — полный DTO (id записи размещения, incl. `hide`).
 */
export const fetchPacedDetailModuleDto = async (pacedDetailModuleId, {bypassCache = false} = {}) => {
    if (!hasRealId(pacedDetailModuleId)) return null;
    const cacheKey = String(pacedDetailModuleId);
    if (!bypassCache && pacedDetailModuleByIdCache.has(cacheKey)) {
        return pacedDetailModuleByIdCache.get(cacheKey);
    }
    try {
        const response = await PacedDetailModulesService.getById(pacedDetailModuleId);
        const data = response?.data ?? null;
        pacedDetailModuleByIdCache.set(cacheKey, data);
        return data;
    } catch (err) {
        const status = err?.response?.status;
        if (status === 404 || status === 204) {
            pacedDetailModuleByIdCache.set(cacheKey, null);
            return null;
        }
        console.warn("module-3d: paced-detail-modules get failed", err);
        return null;
    }
};

/** Деталь уже размещена на сцене (есть pacedDetailModule с позой в составе). */
export const isModuleDetailPlacedDto = (detail) => {
    const row = stampCompositionDetailId(detail);
    const paced = parsePacedDetailModuleDto(row?.pacedDetailModule);
    return Boolean(paced?.localPosition && paced?.axes);
};

/**
 * Размещённые детали: поза и hide уже приходят в GET by-module-id (`pacedDetailModule`).
 * GET paced-detail-modules/{id} — только при bypassCache (после сохранения) или если в строке нет полного DTO.
 */
export const resolveModuleDetailWithPlacement = async (detail, {bypassCache = false} = {}) => {
    if (!detail || !isModuleDetailPlacedDto(detail)) return detail;
    const row = stampCompositionDetailId(detail);
    const pacedId = getPacedDetailModuleId(row);
    if (!hasRealId(pacedId)) return row;

    const embedded = row.pacedDetailModule;
    if (!bypassCache && embedded && typeof embedded === "object" && embedded.anchorPoint) {
        pacedDetailModuleByIdCache.set(String(pacedId), embedded);
        return row;
    }

    const pacedDetailModule = await fetchPacedDetailModuleDto(pacedId, {bypassCache});
    if (!pacedDetailModule) return row;
    return {...row, pacedDetailModule};
};

/** Поза из `pacedDetailModule` в строке GET by-module-id (без GET paced-detail-modules/{id}). */
export const parsePacedPlacementFromDetailRow = (detail) => {
    const row = stampCompositionDetailId(detail);
    const dto = row?.pacedDetailModule;
    if (!dto || typeof dto !== "object" || dto.anchorPoint == null) {
        return null;
    }
    const pacedId = getPacedDetailModuleId(row);
    if (hasRealId(pacedId)) {
        pacedDetailModuleByIdCache.set(String(pacedId), dto);
    }
    return parsePacedDetailModuleDto(dto);
};

/**
 * GET paced-detail-modules/{id} — поза для snap (parsed + raw DTO).
 */
export const fetchPacedDetailPlacement = async (pacedUrlId) => {
    const data = await fetchPacedDetailModuleDto(pacedUrlId);
    if (!data) return null;
    return parsePacedDetailModuleDto(data);
};

/**
 * POST — создать экземпляр (query detailId/moduleId, body без id).
 * PUT — path и body.id = paced-detail-module.id (не id детали 2D).
 * @returns {object|null} DTO ответа
 */
export const persistPacedDetailPlacement = async ({
    moduleId,
    detailId,
    localPosition,
    axes,
    faceFlip = false,
    referenceAxes = null,
    localRotationAngles = null,
    existingDto = null,
    /** Id созданного экземпляра — path и body PUT */
    pacedBodyId = null,
}) => {
    if (moduleId == null || moduleId === "" || detailId == null || detailId === "") {
        return null;
    }

    const bodyId = hasRealId(pacedBodyId)
        ? pacedBodyId
        : (hasRealId(existingDto?.id) && String(existingDto.id) !== String(detailId)
            ? existingDto.id
            : null);

    try {
        // Уже есть размещение → PUT /{pacedId}.
        if (bodyId != null) {
            const payload = buildPacedDetailModulePayload({
                localPosition,
                axes,
                faceFlip,
                referenceAxes,
                localRotationAngles,
                existingDto,
                forCreate: false,
            });
            const body = {...payload, id: bodyId};
            const response = await PacedDetailModulesService.update(bodyId, detailId, body);
            const data = response?.data ?? null;
            if (data != null) {
                cachePacedDetailModuleDto(bodyId, data);
            }
            return data;
        }

        // Ещё нет экземпляра → POST (body без id).
        const payload = buildPacedDetailModulePayload({
            localPosition,
            axes,
            faceFlip,
            referenceAxes,
            localRotationAngles,
            existingDto: null,
            forCreate: true,
        });
        const response = await PacedDetailModulesService.save(detailId, moduleId, payload);
        const data = response?.data;
        // id должен прийти только из ответа POST (data.id / pacedDetailModuleId / или location header).
        // Никаких fallback на detailId.
        const id = extractPacedRecordId(data, response, detailId);
        if (id == null) return null;
        const result = data != null && typeof data === "object" && !Array.isArray(data)
            ? {...data, id}
            : {id};
        cachePacedDetailModuleDto(id, result);
        return result;
    } catch (err) {
        console.warn("module-3d: paced-detail-modules persist failed", err);
        // Для POST при добавлении детали нам нужна обработка ошибки в UI:
        // показать сообщение и убрать деталь со сцены.
        throw err;
    }
};

/** DTO с бэка для PUT (id точек и массивы variables). */
export const resolvePacedExistingDto = (inst) => {
    if (!inst || typeof inst !== "object") return null;
    const fromInst = inst.pacedDetailModule?.raw ?? inst.pacedDetailModule;
    if (fromInst && typeof fromInst === "object" && fromInst.anchorPoint != null) {
        return fromInst;
    }
    const fromDetail = inst.detail?.pacedDetailModule;
    if (fromDetail && typeof fromDetail === "object") return fromDetail;
    return null;
};

/**
 * PATCH paced-detail-modules/{id}/hide — body: boolean.
 */
export const updatePacedDetailHidden = async ({
    pacedDetailModuleId,
    hide,
    existingDto = null,
}) => {
    if (!hasRealId(pacedDetailModuleId)) return null;
    const prev = existingDto && typeof existingDto === "object" ? existingDto : {};
    try {
        const response = await PacedDetailModulesService.setHide(pacedDetailModuleId, hide);
        const data = response?.data ?? null;
        if (data != null && typeof data === "object" && data.anchorPoint != null) {
            invalidatePacedDetailModuleCache(pacedDetailModuleId);
            pacedDetailModuleByIdCache.set(String(pacedDetailModuleId), data);
            return data;
        }
        const nextDto = {
            ...prev,
            id: pacedDetailModuleId,
            hide: Boolean(hide),
        };
        pacedDetailModuleByIdCache.set(String(pacedDetailModuleId), nextDto);
        return nextDto;
    } catch (err) {
        console.warn("module-3d: paced-detail-modules hidden update failed", err);
        return null;
    }
};

export const hideAllPacedDetails = async (moduleId) => {
    if (moduleId == null || moduleId === "") return false;
    try {
        await PacedDetailModulesService.showOrHideAllDetails(moduleId, true);
        invalidateAllPacedDetailModuleCache();
        return true;
    } catch (err) {
        console.warn("module-3d: paced-detail-modules show-or-hide-all-details failed", err);
        return false;
    }
};

export const showAllPacedDetails = async (moduleId) => {
    if (moduleId == null || moduleId === "") return false;
    try {
        await PacedDetailModulesService.showOrHideAllDetails(moduleId, false);
        invalidateAllPacedDetailModuleCache();
        return true;
    } catch (err) {
        console.warn("module-3d: paced-detail-modules show-or-hide-all-details failed", err);
        return false;
    }
};

export const hideAllPacedDetailsExcept = async (moduleId, pacedDetailModuleId) => {
    if (moduleId == null || moduleId === "" || !hasRealId(pacedDetailModuleId)) return false;
    try {
        await PacedDetailModulesService.hideAllExcept(pacedDetailModuleId, moduleId, true);
        invalidateAllPacedDetailModuleCache();
        return true;
    } catch (err) {
        console.warn("module-3d: paced-detail-modules hide-all-except failed", err);
        return false;
    }
};

export const updatePacedDetailScenePose = async ({
    pacedDetailModuleId,
    detailId,
    localPosition,
    axes,
    faceFlip = false,
    referenceAxes = null,
    localRotationAngles = null,
    existingDto = null,
}) => {
    if (!hasRealId(pacedDetailModuleId) || !hasRealId(detailId)) return null;
    const prev = existingDto && typeof existingDto === "object" ? existingDto : {};
    const payload = buildPacedDetailModulePayload({
        localPosition,
        axes,
        faceFlip,
        referenceAxes,
        localRotationAngles,
        existingDto: prev,
        forCreate: false,
    });
    const body = {
        ...payload,
        id: pacedDetailModuleId,
    };
    try {
        const response = await PacedDetailModulesService.update(
            pacedDetailModuleId,
            detailId,
            body,
        );
        const data = response?.data ?? null;
        if (data != null) {
            cachePacedDetailModuleDto(pacedDetailModuleId, data);
        }
        return data;
    } catch (err) {
        console.warn("module-3d: paced-detail-modules update failed", err);
        return null;
    }
};

const COORD_KEYS = ["x", "y", "z"];

/** Строка поля формы из PointDto (число или pattern переменной x/y/z). */
export const pointCoordToFormString = (point, coordName) => {
    if (!point || typeof point !== "object") return "0";
    const format = (value) => {
        const text = String(value ?? "").trim();
        if (!isPlainNumberString(text)) return text;
        const number = Number(text);
        if (!Number.isFinite(number) || Math.abs(number) < 1e-12) return "0";
        return String(number);
    };
    const pointVars = asVariablesList(point.variables);
    const named = pointVars.find((v) => v?.name === coordName);
    if (named?.pattern != null && String(named.pattern).trim() !== "") {
        return format(named.pattern);
    }
    if (named?.value != null && String(named.value).trim() !== "") {
        return format(named.value);
    }
    const raw = point[coordName];
    if (raw == null || raw === "") return "0";
    return format(raw);
};

export const emptyPointForm = () => ({x: "0", y: "0", z: "0"});

export const pointToFormCoords = (point) => ({
    x: pointCoordToFormString(point, "x"),
    y: pointCoordToFormString(point, "y"),
    z: pointCoordToFormString(point, "z"),
});

/**
 * Форма точки → PointDto.
 * Число → строка в x/y/z; формула → pattern в variables (id сохраняются).
 */
export const buildPointFromFormCoords = (prevPoint, coords = {}) => {
    const point = {};
    if (prevPoint?.id != null) {
        point.id = prevPoint.id;
    }

    const prevVariables = asVariablesList(prevPoint?.variables);
    const coordPatterns = [];
    for (const key of COORD_KEYS) {
        const raw = String(coords?.[key] ?? "").trim();
        if (raw && isPlainNumberString(raw)) {
            point[key] = raw;
        } else if (raw) {
            point[key] = String(prevPoint?.[key] ?? "0");
            const previous = prevVariables.find((variable) => variable?.name === key);
            coordPatterns.push({...previous, name: key, pattern: raw});
        } else {
            point[key] = "0";
        }
    }

    const prevOther = prevVariables
        .filter((v) => v && !COORD_KEYS.includes(v.name));
    point.variables = [...prevOther, ...coordPatterns];
    return point;
};

/** DTO с бэка → поля вкладки «Основные». */
export const pacedDtoToMainForm = (dto) => ({
    anchor: pointToFormCoords(dto?.anchorPoint),
    axesX: pointToFormCoords(dto?.axesX),
    axesY: pointToFormCoords(dto?.axesY),
    axesZ: pointToFormCoords(dto?.axesZ),
});

/**
 * Форма + исходный DTO → тело PUT нового формата.
 */
export const buildPacedMainFormPayload = (form, existingDto, pacedId = null) => {
    const prev = existingDto && typeof existingDto === "object" ? existingDto : {};
    const id = hasRealId(pacedId) ? pacedId : prev.id;
    return {
        id,
        anchorPoint: buildPointFromFormCoords(prev.anchorPoint, form?.anchor),
        axesX: buildPointFromFormCoords(prev.axesX, form?.axesX),
        axesY: buildPointFromFormCoords(prev.axesY, form?.axesY),
        axesZ: buildPointFromFormCoords(prev.axesZ, form?.axesZ),
        variables: asVariablesList(prev.variables),
        hide: Boolean(prev.hide),
    };
};

const sceneAxisFormCoords = (axes, key) => {
    const tuple = axisTuple(axes, key);
    return {
        x: String(tuple[0]),
        y: String(tuple[1]),
        z: String(tuple[2]),
    };
};

/**
 * Поза на 3D-сцене → тело PUT (как «Применить» на вкладке Размещение / PacedModulePlacementPanel).
 */
export const buildPacedSceneUpdatePayload = ({
    localPosition,
    axes,
    faceFlip = false,
    referenceAxes = null,
    localRotationAngles = null,
    existingDto = null,
    pacedId = null,
}) => {
    const persistAxes = axesForPersist(axes, faceFlip);
    const form = {
        anchor: {
            x: String(metersToMm(localPosition?.x)),
            y: String(metersToMm(localPosition?.y)),
            z: String(metersToMm(localPosition?.z)),
        },
        axesX: sceneAxisFormCoords(persistAxes, "x"),
        axesY: sceneAxisFormCoords(persistAxes, "y"),
        axesZ: sceneAxisFormCoords(persistAxes, "z"),
    };
    const base = buildPacedMainFormPayload(form, existingDto, pacedId);
    return {
        ...base,
        variables: mergePanelRotationVariables(
            base.variables,
            referenceAxes,
            localRotationAngles,
            persistAxes,
        ),
    };
};
